/**
 * GRIDHEIMR Dashboard Main Page
 *
 * Overview page showing bot statistics and quick actions.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { BotList } from "../components/BotList";
import { useBots } from "../hooks/useBots";
import { BLUE, YELLOW, PIXEL, MONO } from "../theme";

/**
 * Dashboard Page Component
 *
 * Displays an overview of the user's bots and system status.
 */
const STATS = [
  { icon: "🤖", label: "Total Bots",  key: "total"   as const, color: BLUE   },
  { icon: "▶",  label: "Running",     key: "running"  as const, color: "#00c97a" },
  { icon: "⏸",  label: "Stopped",    key: "stopped"  as const, color: "rgba(255,255,255,0.4)" },
  { icon: "✕",  label: "Errors",     key: "errors"   as const, color: "#ff4d4d" },
];

const ACTIONS = [
  { icon: "＋", label: "CREATE NEW BOT",      route: "/bots/create",  primary: true  },
  { icon: "▤",  label: "VIEW ALL BOTS",        route: "/bots",         primary: false },
  { icon: "🔑", label: "MANAGE CREDENTIALS",  route: "/credentials",  primary: false },
  { icon: "📡", label: "HATCHERY LAB",         route: "/lab",          primary: false },
];

export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { bots, loading } = useBots(undefined, 5000);

  // Calculate stats from bot data
  const stats = {
    total: bots.length,
    running: bots.filter((bot) => bot.status === "running").length,
    stopped: bots.filter((bot) => bot.status === "stopped").length,
    errors: bots.filter((bot) => bot.status === "failed").length,
  };

  return (
    <div style={styles.container}>
      {/* Page Header */}
      <div style={styles.header}>
        <p style={styles.sectionLabel}>{"// OVERVIEW"}</p>
        <h1 style={styles.title}>Mission Control</h1>
        <p style={styles.subtitle}>
          Welcome back,{" "}
          <span style={{ color: YELLOW }}>{user?.email ?? user?.username}</span>
        </p>
      </div>

      {/* Stats Cards */}
      <div style={styles.statsGrid}>
        {STATS.map((s) => (
          <div key={s.key} style={{ ...styles.statCard, borderColor: `${s.color}40` }}>
            <div style={{ ...styles.statAccent, background: s.color }} />
            <div style={styles.statContent}>
              <div style={{ ...styles.statValue, color: s.color }}>
                {loading ? "—" : stats[s.key]}
              </div>
              <div style={styles.statLabel}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div style={styles.section}>
        <p style={styles.sectionLabel}>{"// QUICK ACTIONS"}</p>
        <div style={styles.actionsGrid}>
          {ACTIONS.map((a) => (
            <button
              key={a.route}
              style={a.primary ? styles.actionBtnPrimary : styles.actionBtn}
              onClick={() => navigate(a.route)}>
              <span style={styles.actionIcon}>{a.icon}</span>
              <span style={styles.actionLabel}>{a.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Bot List */}
      <div style={styles.section}>
        <p style={styles.sectionLabel}>{"// YOUR BOTS"}</p>
        <BotList />
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { width: "100%" },
  header: { marginBottom: "2.5rem" },
  sectionLabel: {
    fontFamily: MONO,
    fontSize: "0.7rem",
    color: "rgba(255,255,255,0.3)",
    letterSpacing: "0.15em",
    margin: "0 0 0.75rem 0",
  },
  title: {
    fontFamily: PIXEL,
    fontSize: "clamp(0.8rem, 2.5vw, 1.2rem)",
    fontWeight: 400,
    margin: "0 0 0.75rem 0",
    color: "#fff",
    letterSpacing: "0.06em",
    textShadow: `0 0 15px rgba(0,106,167,0.4)`,
  },
  subtitle: {
    fontFamily: MONO,
    fontSize: "0.85rem",
    margin: 0,
    color: "rgba(255,255,255,0.5)",
    letterSpacing: "0.05em",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "1px",
    background: `rgba(0,106,167,0.08)`,
    border: `1px solid rgba(0,106,167,0.15)`,
    marginBottom: "2.5rem",
  },
  statCard: {
    display: "flex",
    alignItems: "center",
    gap: "1.25rem",
    background: "#000a12",
    padding: "1.5rem",
    border: "1px solid transparent",
    transition: "border-color 0.3s",
    position: "relative",
    overflow: "hidden",
  },
  statAccent: {
    width: "3px",
    height: "40px",
    flexShrink: 0,
  },
  statContent: { flex: 1 },
  statValue: {
    fontFamily: PIXEL,
    fontSize: "1.4rem",
    fontWeight: 400,
    marginBottom: "0.4rem",
    letterSpacing: "0.05em",
  },
  statLabel: {
    fontFamily: MONO,
    fontSize: "0.7rem",
    color: "rgba(255,255,255,0.45)",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
  },
  section: { marginBottom: "2.5rem" },
  actionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "1px",
    background: `rgba(0,106,167,0.06)`,
    border: `1px solid rgba(0,106,167,0.12)`,
  },
  actionBtnPrimary: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "1.25rem 1.5rem",
    background: YELLOW,
    border: "none",
    cursor: "pointer",
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    color: "#000",
    letterSpacing: "0.1em",
    transition: "all 0.2s ease",
    textAlign: "left",
    boxShadow: `0 0 12px rgba(254,204,0,0.25)`,
  },
  actionBtn: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "1.25rem 1.5rem",
    background: "#000a12",
    border: "none",
    cursor: "pointer",
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    color: "rgba(255,255,255,0.6)",
    letterSpacing: "0.1em",
    transition: "all 0.2s ease",
    textAlign: "left",
  },
  actionIcon: {
    fontSize: "1.1rem",
    flexShrink: 0,
    color: BLUE,
  },
  actionLabel: { flex: 1 },
};
